import { create } from 'zustand';

import { Validation } from '../types';

interface AttributesStatesStore {
    hidden: { [key: string]: boolean };
    readonly: { [key: string]: boolean };
    isInitialized: boolean;
}

interface Actions {
    setHidden: (attributePath: string[] | string) => void;
    setReadonly: (attributePath: string[] | string) => void;
    clearState: (attributePath?: string[] | string) => void;
    setInitialized: (initialized: boolean) => void;
    initializeWithStates: (attributeStates: string[]) => void;
    // Apply a live validation attributeStates map in a single set() (LAX-9602)
    applyAttributeStates: (attributeStates: Record<string, string[]>) => void;
}

const initialState: AttributesStatesStore = {
    hidden: {},
    readonly: {},
    isInitialized: false,
};

const useAttributesStateStore = create<AttributesStatesStore & Actions>((set) => ({
    ...initialState,
    setHidden: (attributePaths: string[] | string) => {
        set((state) => {
            const updatedHidden = { ...state.hidden };

            // Handle both single and multiple attribute paths
            const paths = Array.isArray(attributePaths) ? attributePaths : [attributePaths];

            paths.forEach((attributePath) => {
                updatedHidden[attributePath] = true;
            });

            return {
                ...state,
                hidden: updatedHidden,
                isInitialized: true,
            };
        });
    },
    setReadonly: (attributePaths: string[] | string) => {
        set((state) => {
            const updatedReadonly = { ...state.readonly };

            // Handle both single and multiple attribute paths
            const paths = Array.isArray(attributePaths) ? attributePaths : [attributePaths];

            paths.forEach((attributePath) => {
                updatedReadonly[attributePath] = true;
            });

            return {
                ...state,
                readonly: updatedReadonly,
                isInitialized: true,
            };
        });
    },
    clearState: (attributePaths?: string[] | string) => {
        set((state) => {
            if (!attributePaths) {
                return {
                    ...state,
                    hidden: {},
                    readonly: {},
                };
            }
            // Handle both single and multiple attribute paths
            const paths = Array.isArray(attributePaths) ? attributePaths : [attributePaths];

            const updatedHidden = { ...state.hidden };
            const updatedReadonly = { ...state.readonly };

            paths.forEach((attributePath) => {
                delete updatedHidden[attributePath];
                delete updatedReadonly[attributePath];
            });

            return {
                ...state,
                hidden: updatedHidden,
                readonly: updatedReadonly,
            };
        });
    },
    setInitialized: (initialized: boolean) => {
        set((state) => ({
            ...state,
            isInitialized: initialized,
        }));
    },
    initializeWithStates: (attributeStates: string[]) => {
        set((state) => {
            const updatedHidden = { ...state.hidden };
            const updatedReadonly = { ...state.readonly };

            // Merge states per attribute so the same attribute in multiple entries is combined
            const statesByAttribute = new Map<string, Set<string>>();
            attributeStates.forEach((attributeState) => {
                const [attributeName, statesString] = attributeState.split(':');
                const states = statesString?.split(',').map((s) => s.trim()) || [];
                const name = attributeName?.trim();
                if (!name) return;
                if (!statesByAttribute.has(name)) {
                    statesByAttribute.set(name, new Set());
                }
                states.forEach((s) => statesByAttribute.get(name)?.add(s));
            });

            statesByAttribute.forEach((states, attributeName) => {
                if (states.has('hidden')) {
                    updatedHidden[attributeName] = true;
                } else {
                    delete updatedHidden[attributeName];
                }
                if (states.has('readonly')) {
                    updatedReadonly[attributeName] = true;
                } else {
                    delete updatedReadonly[attributeName];
                }
            });

            return {
                ...state,
                hidden: updatedHidden,
                readonly: updatedReadonly,
                isInitialized: true,
            };
        });
    },
    applyAttributeStates: (attributeStates) => {
        const entries = Object.entries(attributeStates);
        if (entries.length === 0) {
            return;
        }

        set((state) => {
            const updatedHidden = { ...state.hidden };
            const updatedReadonly = { ...state.readonly };
            let didSetFlag = false;

            entries.forEach(([attributeName, states]) => {
                delete updatedHidden[attributeName];
                delete updatedReadonly[attributeName];
                if (states.includes('hidden')) {
                    updatedHidden[attributeName] = true;
                    didSetFlag = true;
                }
                if (states.includes('readonly')) {
                    updatedReadonly[attributeName] = true;
                    didSetFlag = true;
                }
            });

            return {
                ...state,
                hidden: updatedHidden,
                readonly: updatedReadonly,
                isInitialized: didSetFlag ? true : state.isInitialized,
            };
        });
    }
}));

export { useAttributesStateStore, type Validation };

